import { useCallback } from 'react';
import * as LancamentosAPI from '../../../../api/lancamentos';
import { relatorios } from '../../../../api/relatorios';
import { printPdf } from '../../../../utils/funcoes';

export function useRelatoriosFinanceiroModule({ mrt, urlParams, lancamentos, empresaIds }) {
    const buildUrlSearchParams = useCallback(() => {
        return new URLSearchParams({
            start: `${mrt.pagination.pageIndex * mrt.pagination.pageSize}`,
            size: `${mrt.pagination.pageSize}`,
            filters: JSON.stringify(mrt.columnFilters ?? []),
            filtersMode: JSON.stringify(mrt.columnFilterFns ?? []),
            globalFilter: mrt.globalFilter ?? '',
            sorting: JSON.stringify(mrt.sorting ?? []),
            tipo: urlParams.get('tipo') ?? '',
        });
    }, [mrt, urlParams]);

    const getLancamentosFiltradosPorSelecao = useCallback(
        (table) => {
            const uuids = table.getSelectedRowModel().flatRows.map((row) => row.id);

            return uuids.length > 0 ? lancamentos.filter((lancamento) => uuids.includes(lancamento.uuid)) : lancamentos;
        },
        [lancamentos]
    );

    const fluxoCaixa = useCallback(async () => {
        if (!lancamentos[0]) return;

        const dataVencimentoFilter = mrt.columnFilters.find((f) => f.id === 'dataVencimento');
        const dataInicial = dataVencimentoFilter?.value?.[0];
        const dataFinal = dataVencimentoFilter?.value?.[1];

        const obj = {
            empresaId: lancamentos[0].empresaId,
            dataInicial,
            dataFinal,
        };

        const response = await relatorios('fluxoCaixa', 'fluxoCaixa', obj);
        printPdf(response.data);
    }, [lancamentos, mrt.columnFilters]);
    const imprimirLancamentos = useCallback(
        async ({ table, baixados }) => {
            const lancamentosFiltrados = getLancamentosFiltradosPorSelecao(table);
            const urlSearchParams = buildUrlSearchParams();

            const response = baixados
                ? await LancamentosAPI.relatorioLancamentosBaixados(urlSearchParams, lancamentosFiltrados)
                : await LancamentosAPI.relatorio(urlSearchParams, lancamentosFiltrados);

            printPdf(response.data);
        },
        [buildUrlSearchParams, getLancamentosFiltradosPorSelecao]
    );

    const imprimirRecibos = useCallback(
        async (table) => {
            const lancamentosFiltrados = getLancamentosFiltradosPorSelecao(table);
            const urlSearchParams = buildUrlSearchParams();

            const response = await LancamentosAPI.recibos(urlSearchParams, lancamentosFiltrados);
            printPdf(response.data);
        },
        [buildUrlSearchParams, getLancamentosFiltradosPorSelecao]
    );

    const vendasDiaria = useCallback(async () => {
        if (!lancamentos[0]) return;

        const dataVencimentoFilter = mrt.columnFilters.find((f) => f.id === 'dataVencimento');
        const dataInicial = dataVencimentoFilter?.value?.[0];
        const dataFinal = dataVencimentoFilter?.value?.[1];

        const obj = {
            empresaId: lancamentos[0].empresaId,
            dataInicial,
            dataFinal,
        };

        const response = await relatorios('vendasDiaria', 'vendasDiaria', obj);
        printPdf(response.data);
    }, [lancamentos, mrt.columnFilters]);

    const vendasPeriodo = useCallback(async () => {
        if (!lancamentos[0]) return;

        const dataVencimentoFilter = mrt.columnFilters.find((f) => f.id === 'dataVencimento');
        const dataInicial = dataVencimentoFilter?.value?.[0];
        const dataFinal = dataVencimentoFilter?.value?.[1];

        const obj = {
            empresaId: lancamentos[0].empresaId,
            dataInicial,
            dataFinal,
        };

        const response = await relatorios('vendasAgrupadasByForma', 'vendasAgrupadasByForma', obj);
        printPdf(response.data);
    }, [lancamentos, mrt.columnFilters]);

    const conciliacaoContas = useCallback(async () => {
        if (!lancamentos[0]) return;

        const dataVencimentoFilter = mrt.columnFilters.find((f) => f.id === 'dataVencimento');
        const dataInicial = dataVencimentoFilter?.value?.[0];
        const dataFinal = dataVencimentoFilter?.value?.[1];

        const obj = {
            empresaId: lancamentos[0].empresaId,
            empresaIds,
            dataInicial,
            dataFinal,
        };

        const response = await relatorios('conciliacaoContas', 'conciliacaoContas', obj);

        const pdfBlob = new Blob([response.data], {
            type: 'application/pdf',
        });

        const pdfUrl = URL.createObjectURL(pdfBlob);
        window.open(pdfUrl);
    }, [lancamentos, mrt.columnFilters, empresaIds]);

    return {
        fluxoCaixa,
        imprimirLancamentos,
        imprimirRecibos,
        vendasDiaria,
        vendasPeriodo,
        conciliacaoContas,
    };
}
